import type { GHGScope } from '../types';
import { SCOPE3_CATEGORIES_META } from '../types/ghg-settings';

export type ActivityScopeOptionValue = '1' | '2' | '3';
export type Scope2EnergyType = 'electricity' | 'steam' | 'heating' | 'cooling';
type CatalogTranslateFn = (key: string, options?: Record<string, unknown>) => string;

export interface ActivityEntryCategoryOption {
  value: string;
  label: string;
  helperText: string;
  scope3Category?: number;
  /** When set, selecting this category should navigate to this path instead of continuing the form. */
  redirectTo?: string;
  translationKey?: string;
}

const ACTIVITY_ENTRY_SCOPE_OPTIONS = [
  { value: '1', label: 'Scope 1 - Direct Emissions' },
  { value: '2', label: 'Scope 2 - Purchased Energy' },
  { value: '3', label: 'Scope 3 - Value Chain' },
] satisfies readonly { value: ActivityScopeOptionValue; label: string }[];

function translateLabel(
  t: CatalogTranslateFn | undefined,
  key: string,
  defaultValue: string,
  interpolation?: Record<string, unknown>
): string {
  return t ? t(key, { defaultValue, ...interpolation }) : defaultValue;
}

export function getActivityEntryScopeOptions(
  t?: CatalogTranslateFn
): { value: ActivityScopeOptionValue; label: string }[] {
  return ACTIVITY_ENTRY_SCOPE_OPTIONS.map((option) => ({
    ...option,
    label: translateLabel(
      t,
      `activityDataImportWizard.scopeOption_scope${option.value}`,
      option.label
    ),
  }));
}

const SCOPE1_CATEGORY_OPTIONS: ActivityEntryCategoryOption[] = [
  {
    value: 'stationary_combustion',
    label: 'Stationary Combustion',
    helperText: 'On-site fuel combustion from boilers, generators, and stationary equipment.',
    translationKey: 'gHGOnboardingWizard.scope1_stationary_combustion',
  },
  {
    value: 'mobile_combustion',
    label: 'Mobile Combustion',
    helperText: 'Fleet fuel use from company cars, trucks, and other vehicles.',
    translationKey: 'gHGOnboardingWizard.scope1_mobile_combustion',
  },
  {
    value: 'process_emissions',
    label: 'Process Emissions',
    helperText: 'Process-related releases from industrial or chemical operations.',
    translationKey: 'gHGOnboardingWizard.scope1_process_emissions',
  },
  {
    value: 'fugitive_emissions',
    label: 'Fugitive Emissions',
    helperText: 'Unintended leaks, including refrigerants and other gases.',
    translationKey: 'gHGOnboardingWizard.scope1_fugitive_emissions',
  },
];

const SCOPE2_CATEGORY_OPTIONS: ActivityEntryCategoryOption[] = [
  {
    value: 'purchased_electricity',
    label: 'Purchased Electricity',
    helperText: 'Grid or utility electricity consumed by your operations.',
    translationKey: 'gHGOnboardingWizard.scope2_purchased_electricity',
  },
  {
    value: 'purchased_steam',
    label: 'Purchased Steam',
    helperText: 'Steam purchased from external providers.',
    translationKey: 'gHGOnboardingWizard.scope2_purchased_steam',
  },
  {
    value: 'purchased_heating',
    label: 'Purchased Heating',
    helperText: 'District heating or externally supplied heat.',
    translationKey: 'gHGOnboardingWizard.scope2_purchased_heating',
  },
  {
    value: 'purchased_cooling',
    label: 'Purchased Cooling',
    helperText: 'District cooling or externally supplied cooling.',
    translationKey: 'gHGOnboardingWizard.scope2_purchased_cooling',
  },
];

const SCOPE3_SELECTION_PREFIX = 'scope3_category_';

function camelToSnake(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}

const SOLD_PRODUCT_CATEGORIES = new Set([10, 11, 12]);

function normalizeScope(scope: GHGScope | ActivityScopeOptionValue): ActivityScopeOptionValue {
  return String(scope) as ActivityScopeOptionValue;
}

export function getCategoryOptionsForScope(
  scope: GHGScope | ActivityScopeOptionValue,
  t?: CatalogTranslateFn
): ActivityEntryCategoryOption[] {
  const normalizedScope = normalizeScope(scope);
  if (normalizedScope === '1') {
    return SCOPE1_CATEGORY_OPTIONS.map((option) => ({
      ...option,
      label: translateLabel(t, option.translationKey ?? '', option.label),
    }));
  }
  if (normalizedScope === '2') {
    return SCOPE2_CATEGORY_OPTIONS.map((option) => ({
      ...option,
      label: translateLabel(t, option.translationKey ?? '', option.label),
    }));
  }
  return SCOPE3_CATEGORIES_META.map((category) => {
    const categoryLabel = translateLabel(
      t,
      `gHGOnboardingWizard.scope3_c${String(category.number)}`,
      category.name
    );
    return {
      value: `${SCOPE3_SELECTION_PREFIX}${String(category.number)}`,
      label: translateLabel(
        t,
        'ghgScope3.categoryTitle',
        `Category ${String(category.number)}: ${categoryLabel}`,
        { number: category.number, label: categoryLabel }
      ),
      helperText: category.description,
      scope3Category: category.number,
      ...(SOLD_PRODUCT_CATEGORIES.has(category.number)
        ? { redirectTo: '/ghg/scope3/sold-products' }
        : {}),
    };
  });
}

export function resolveSelectionToActivityCategory(
  scope: GHGScope | ActivityScopeOptionValue,
  selection: string
): string {
  if (!selection.trim()) return '';

  const normalizedScope = normalizeScope(scope);

  if (selection.startsWith(SCOPE3_SELECTION_PREFIX) && normalizedScope === '3') {
    const parsed = Number(selection.replace(SCOPE3_SELECTION_PREFIX, ''));
    const categoryMeta = SCOPE3_CATEGORIES_META.find((category) => category.number === parsed);
    return categoryMeta ? camelToSnake(categoryMeta.key) : '';
  }

  return selection;
}

export function defaultScope2EnergyTypeForCategory(activityCategory: string): Scope2EnergyType | '' {
  const normalized = activityCategory.trim().toLowerCase();
  if (!normalized) return '';
  if (normalized.includes('steam')) return 'steam';
  if (normalized.includes('cool')) return 'cooling';
  if (normalized.includes('heat')) return 'heating';
  if (normalized.includes('electric')) return 'electricity';
  return '';
}

export function defaultScope3CategoryForSelection(
  scope: GHGScope | ActivityScopeOptionValue,
  selection: string
): number | undefined {
  if (normalizeScope(scope) !== '3') return undefined;
  if (!selection.startsWith(SCOPE3_SELECTION_PREFIX)) return undefined;

  const parsed = Number(selection.replace(SCOPE3_SELECTION_PREFIX, ''));
  if (!Number.isInteger(parsed)) return undefined;
  if (parsed < 1 || parsed > 15) return undefined;
  return parsed;
}

export function getActivityTypePlaceholder(scope: GHGScope, activityCategory: string): string {
  if (scope === 1) {
    if (activityCategory.includes('mobile')) return 'e.g., fleet_diesel, vehicle_distance';
    if (activityCategory.includes('stationary')) return 'e.g., natural_gas_combustion';
    return 'e.g., source_type_or_process';
  }
  if (scope === 2) {
    return 'e.g., utility_bill, meter_reading';
  }
  return 'e.g., supplier_data, travel_distance, purchased_goods';
}

export function getQuantityPlaceholder(scope: GHGScope): string {
  if (scope === 2) return 'e.g., 12500';
  if (scope === 3) return 'e.g., 250';
  return 'e.g., 1500';
}
